// ELS error reporter для quiz-frontend.
// Захватывает window.onerror, unhandledrejection, React ErrorBoundary errors
// и шлёт POST в ELS endpoint с полным контекстом (URL, user-agent, stack).

const ELS_URL = (import.meta.env.VITE_ELS_URL || 'https://api.insoweb.ru/els').replace(/\/$/, '');
const ELS_API_KEY = import.meta.env.VITE_ELS_API_KEY || '';
const ELS_APP_SLUG = import.meta.env.VITE_ELS_APP_SLUG || 'quiz';
const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || 'dev';

let installed = false;

interface LogPayload {
  level: 'error' | 'warn' | 'info';
  message: string;
  stack?: string;
  errorName?: string;
  // Контекст
  url?: string;
  userAgent?: string;
  componentStack?: string;
  metadata?: Record<string, unknown>;
}

async function sendToEls(payload: LogPayload): Promise<void> {
  if (!ELS_API_KEY) return;
  try {
    await fetch(`${ELS_URL}/errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ELS_API_KEY}`,
      },
      body: JSON.stringify({
        appSlug: ELS_APP_SLUG,
        serviceName: 'quiz-frontend',
        level: payload.level.toUpperCase(),
        message: payload.message,
        errorName: payload.errorName,
        stack: payload.stack,
        url: payload.url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
        userAgent: payload.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
        componentStack: payload.componentStack,
        metadata: payload.metadata,
        appVersion: BUILD_VERSION,
        deploymentEnv: 'PRODUCTION',
        timestamp: new Date().toISOString(),
      }),
      keepalive: true, // позволяет дослать при unmount/navigate
    });
  } catch {
    // never crash on logging
  }
}

export function reportError(err: unknown, metadata?: Record<string, unknown>) {
  const error = err instanceof Error ? err : new Error(String(err));
  void sendToEls({
    level: 'error',
    message: error.message || 'Unknown error',
    errorName: error.name,
    stack: error.stack,
    metadata,
  });
}

export function reportWarning(message: string, metadata?: Record<string, unknown>) {
  void sendToEls({ level: 'warn', message, metadata });
}

// Установка глобальных handler'ов: window.onerror, unhandledrejection.
// Вызывается один раз в main.tsx.
export function installGlobalErrorHandlers() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    void sendToEls({
      level: 'error',
      message: event.message || 'window.error',
      errorName: event.error?.name,
      stack: event.error?.stack,
      url: window.location.href,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const error = reason instanceof Error ? reason : new Error(String(reason));
    void sendToEls({
      level: 'error',
      message: error.message || 'unhandledrejection',
      errorName: error.name,
      stack: error.stack,
      url: window.location.href,
      metadata: { reason: typeof reason === 'object' ? '[Object]' : String(reason) },
    });
  });
}
